import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Target, Users, Rocket } from 'lucide-react';

const Trophy = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const MantraSection = ({ scrollToSection }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const steps = [
    { 
      title: 'Imagínalo', 
      desc: 'Define la meta que quieres activar',
      icon: Target,
      gradient: 'from-[#fbb040] to-yellow-500',
      delay: 0
    },
    { 
      title: 'Konstruyelo', 
      desc: 'Arma tu plan solo o entre kompas',
      icon: Users,
      gradient: 'from-yellow-400 to-orange-500',
      delay: 0.2
    },
    { 
      title: 'Lógralo', 
      desc: 'Recibe tu meta',
      icon: Trophy,
      gradient: 'from-[#fbb040] to-yellow-600',
      delay: 0.4
    },
  ];

  return (
    <section id="mantra" ref={ref} className="relative py-32 bg-gradient-to-br from-black via-[#1f2d5a] to-black overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        {/* Glowing orbs */}
        <motion.div
          animate={{ 
            scale: [1, 1.5, 1],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#fbb040] rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-yellow-500 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title with animated elements */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
            El camino hacia{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fbb040] via-yellow-300 to-[#fbb040] animate-gradient">
              tú META
            </span>
          </h2>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4 }}
            className="text-2xl text-gray-300 font-bold"
          >
            Tres pasos explosivos para{' '}
            <span className="text-[#fbb040]">lograr lo que deseas</span>
          </motion.p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 100, scale: 0.5 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: step.delay,
                type: "spring",
                bounce: 0.4
              }}
              whileHover={{ 
                scale: 1.05, 
                y: -20,
                rotate: 2,
              }}
              className="group relative"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#fbb040] to-yellow-500 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
              
              {/* Card */}
              <div className="relative bg-gradient-to-br from-black via-gray-900 to-black p-8 rounded-3xl border-2 border-[#fbb040] hover:border-yellow-400 transition-all shadow-2xl overflow-hidden">
                {/* Icon */}
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.2 }}
                  transition={{ duration: 0.6 }}
                  className={`relative w-24 h-24 mx-auto mb-6 bg-gradient-to-br ${step.gradient} rounded-2xl flex items-center justify-center shadow-2xl`}
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-white rounded-2xl opacity-20"
                  />
                  <step.icon size={48} className="text-black relative z-10" strokeWidth={3} />
                </motion.div>
                
                {/* Content */}
                <h3 className="text-4xl font-black text-white mb-4 relative z-10">{step.title}</h3>
                <p className="text-gray-300 text-xl font-medium relative z-10">{step.desc}</p>
                
                {/* Step number */}
                <motion.div
                  animate={{ 
                    opacity: [0.3, 0.6, 0.3],
                    scale: [1, 1.05, 1],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-4 right-4 w-12 h-12 bg-[#fbb040] rounded-full flex items-center justify-center"
                >
                  <span className="text-2xl font-black text-black">{index + 1}</span>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Button with K logos */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ 
              scale: 1.1,
              boxShadow: "0 0 80px rgba(251, 176, 64, 0.8)",
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('planes')}
            className="group relative overflow-hidden bg-gradient-to-r from-[#fbb040] via-yellow-400 to-[#fbb040] text-black px-16 py-8 rounded-full font-black text-2xl shadow-2xl inline-flex items-center gap-4"
          >
            {/* Animated shine effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
            
            {/* Left K logo */}
            <motion.img
              src="https://customer-assets.emergentagent.com/job_f2ee6b04-88a4-4dce-acdb-9e470155c901/artifacts/8mgrjxkj_K%202.png"
              alt="K"
              animate={{ 
                y: [0, -8, 0],
                scale: [1, 1.15, 1]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-14 h-14 relative z-10"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 4px rgba(0, 0, 0, 0.6))' }}
            />
            
            <span className="relative z-10">¡EMPEZAR AHORA!</span>
            
            {/* Right K logo */}
            <motion.img
              src="https://customer-assets.emergentagent.com/job_f2ee6b04-88a4-4dce-acdb-9e470155c901/artifacts/8mgrjxkj_K%202.png"
              alt="K"
              animate={{ 
                y: [0, -8, 0],
                scale: [1, 1.15, 1]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-14 h-14 relative z-10"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 4px rgba(0, 0, 0, 0.6))' }}
            />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default MantraSection;